/* eslint-disable i18next/no-literal-string */
import {Button} from '@dropbox/dig-components/buttons';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {Tabs} from '@dropbox/dig-components/tabs';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useQuery} from '@tanstack/react-query';
import {AdminWireframeService, ApiError} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {getService} from 'utilities';
import {
  formatWorkstreamTimeframeLabel,
  WorkstreamTimeframe,
} from 'views/workstreams/detail/workstreamTimeframe';
import {EmptyState} from 'views/workstreams/page/WorkstreamsControls';

import {WireframeCompanyView} from './WireframeCompanyView';
import {WireframePillarView} from './WireframePillarView';
import {toNumber} from './wireframeReportFormat';
import {wireframeReportQueryKey} from './wireframeReportQuery';
import {UnpricedNote} from './WireframeUnpricedNote';

const COMPANY_TAB = 'company';
const pillarTab = (id: number) => `pillar:${id}`;

const errorStatus = (error: unknown) => (error instanceof ApiError ? error.status : undefined);

/**
 * /workstreams Wireframes: Finance's read-only headcount and spend
 * report for the half. Company, or one pillar, follows the rail's pillar (the
 * tabs pick it the same way), so a pillar's tab is linkable. Mount it only for
 * Finance admins: it fetches as soon as it renders.
 */
export const WireframesPane = ({
  timeframe,
  pillarName,
  onSelectPillar,
  hrefForWorkstream,
}: {
  timeframe: WorkstreamTimeframe;
  // The rail's pillar; null (or one the report doesn't have) is Company.
  pillarName: string | null;
  onSelectPillar: (pillarName: string | null) => void;
  hrefForWorkstream: (id: number) => string;
}) => {
  const query = useQuery({
    queryKey: wireframeReportQueryKey(timeframe),
    queryFn: () =>
      getService(AdminWireframeService).getWireframeReportApiV1AdminWireframeReportGet(timeframe),
    // Other Finance admins edit the inputs, so reopening the view refetches
    // (showing the cached report meanwhile) instead of the app's 30-minute default.
    staleTime: 0,
    // A denied or unplanned half won't change on retry.
    retry: (failureCount, error) => (errorStatus(error) ?? 500) >= 500 && failureCount < 2,
  });
  const report = query.data;
  const timeframeLabel = formatWorkstreamTimeframeLabel(timeframe);

  if (query.isPending) {
    return (
      <Box display="flex" justifyContent="center" padding="24">
        <Spinner aria-label="Loading wireframe report" />
      </Box>
    );
  }
  // The half has no planning root yet.
  if (errorStatus(query.error) === 404) {
    return (
      <EmptyState
        title="No plan for this half"
        detail={`${timeframeLabel} has no planning narrative to report on. Try another timeframe.`}
      />
    );
  }
  if (query.isError || !report) {
    return (
      <Layout.MaxWidth width="wide" inset>
        <Box paddingY="24">
          <SystemBanner tone="alert">
            <Box display="flex" alignItems="center" justifyContent="space-between" columnGap="16">
              <Text>
                {errorStatus(query.error) === 403
                  ? 'The wireframe report needs Finance access.'
                  : 'Unable to load the wireframe report.'}
              </Text>
              <Button variant="transparent" onClick={() => void query.refetch()}>
                Retry
              </Button>
            </Box>
          </SystemBanner>
        </Box>
      </Layout.MaxWidth>
    );
  }
  if (report.pillars.length === 0) {
    return (
      <EmptyState
        title="No pillars in this half"
        detail="Wireframes summarize headcount by pillar. Try another timeframe."
      />
    );
  }

  const pillar = report.pillars.find((candidate) => candidate.name === pillarName);
  const selectedTab = pillar ? pillarTab(pillar.pillar_workstream_id) : COMPANY_TAB;
  const unpriced = toNumber(report.spend.unpriced_headcount_fte);

  return (
    <Layout.MaxWidth width="wide" inset>
      <Box paddingTop="16" paddingBottom="32">
        <Tabs
          selectedTab={selectedTab}
          onSelection={(tabId: string) =>
            onSelectPillar(
              report.pillars.find(
                (candidate) => pillarTab(candidate.pillar_workstream_id) === tabId
              )?.name ?? null
            )
          }
        >
          <Tabs.Group hasHorizontalPadding={false}>
            <Tabs.Tab id={COMPANY_TAB}>Company</Tabs.Tab>
            {report.pillars.map((candidate) => (
              <Tabs.Tab
                key={candidate.pillar_workstream_id}
                id={pillarTab(candidate.pillar_workstream_id)}
              >
                {candidate.name}
              </Tabs.Tab>
            ))}
          </Tabs.Group>
          <Tabs.Panel tabId={selectedTab}>
            <Box paddingTop="24">
              {pillar ? (
                <WireframePillarView
                  pillar={pillar}
                  timeframeLabel={timeframeLabel}
                  hrefForWorkstream={hrefForWorkstream}
                />
              ) : (
                <Stack gap="16">
                  {unpriced > 0 && <UnpricedNote fte={unpriced} />}
                  <WireframeCompanyView
                    report={report}
                    timeframeLabel={timeframeLabel}
                    hrefForWorkstream={hrefForWorkstream}
                  />
                </Stack>
              )}
            </Box>
          </Tabs.Panel>
        </Tabs>
      </Box>
    </Layout.MaxWidth>
  );
};
